import { DateTime } from 'luxon'
import DailyHours from './DailyHours.js'

import HoursRange from './HoursRange.js'
// eslint-disable-next-line import/no-cycle
import OperatingHours from './OperatingHours.js'
import WeeklyHours from './WeeklyHours.js'

const OVERRIDE_STRING_FORMAT = 'yyyy-LL-dd'
const HOURS_STRING_FORMAT = 'hh:mma'

function getHoursForDate( model, time = new Date() ) {
  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )

  if ( !weeklyHours ) {
    return null
  }
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  if ( !dayInZone ) {
    return null
  }

  let dailyHours = WeeklyHours[`get${dayInZone}`]?.( weeklyHours )
  const override = OperatingHours.getOverride( model, dateInZone )
  dailyHours = override || dailyHours

  return dailyHours
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {boolean|null} Whether or not the operating hours are open given `date`
 */
function getIsOpen( model, time ) {
  const dailyHours = getHoursForDate( model, time )
  if ( !dailyHours ) {
    return null
  }

  const timezone = OperatingHours.getTimezone( model )
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )

  const containingRange = getContainingRange( timeInZone, timezone, DailyHours.getRanges( dailyHours ) )

  return DailyHours.getOpen( dailyHours ) && containingRange != null
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {string|null} The name of the day of the week or override (if any) which matches the `time` used for evaluation
 */
function getMatchingDayName( model, time ) {
  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  if ( !dayInZone ) {
    return null
  }

  let dailyHours = WeeklyHours[`get${dayInZone}`]?.( weeklyHours )
  const override = OperatingHours.getOverride( model, dateInZone )
  dailyHours = override || dailyHours

  return dailyHours.name
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {boolean|null} Whether the date matching `time` is an override or not
 */
function getIsOverride( model, time ) {
  const timezone = OperatingHours.getTimezone( model )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  if ( !dayInZone ) {
    return null
  }
  const override = OperatingHours.getOverride( model, dateInZone )

  return !!override
}

/**
 * @typedef {object} UnitValue
 * @property {string} type The type of unit
 * @property {string} unit The unit in which this UnitValue is expressed
 * @property {number} value The value of the unit 
 */

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {UnitValue|null} The unit value representing the number of milliseconds until the next open time, or null if already open
 */
function getTimeUntilOpen( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( isOpen ) {
    return null
  }

  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { timeUntilOpen } = getNextOpenAndLastCloseTimes( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return timeUntilOpen
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {UnitValue|null} The unit value representing the time since the last close in milliseconds, or null if open.
 */
function getTimeSinceClose( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( isOpen ) {
    return null
  }
  
  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { timeSinceClose } = getNextOpenAndLastCloseTimes( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return timeSinceClose
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {UnitValue|null} The unit value representing the time until close in milliseconds, or null if already closed
 */
function getTimeUntilClose( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( !isOpen ) {
    return null
  }
  
  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { timeUntilClose } = getUntilClose( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return timeUntilClose
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {Date|null} A JS Date object representing the next close time, or null if already closed.
 */
function getNextCloseTimestamp( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( !isOpen ) {
    return null
  }
  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { nextCloseTimestamp } = getUntilClose( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return nextCloseTimestamp
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {UnitValue|null} The unit value representing the time since last open in milliseconds, or null if closed
 */
function getTimeSinceOpen( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( !isOpen ) {
    return null
  }

  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { timeSinceOpen } = getSinceOpen( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return timeSinceOpen
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {Date|null} A JS Date object representing the last open time, or null if closed.
 */
function getLastOpenTimestamp( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( !isOpen ) {
    return null
  }

  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { lastOpenTimestamp } = getSinceOpen( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return lastOpenTimestamp
}

/**
 * 
* @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {Date|null} A JS Date object representing the next open time, or null if open.
 */
function getNextOpenTimestamp( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( isOpen ) {
    return null
  }

  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { nextOpenTimestamp } = getNextOpenAndLastCloseTimes( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return nextOpenTimestamp
}

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {Date|null} A JS Date object representing the last close time, or null if open.
 */
function getLastCloseTimestamp( model, time ) {
  const isOpen = getIsOpen( model, time )

  if ( isOpen ) {
    return null
  }

  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  const { lastCloseTimestamp } = getNextOpenAndLastCloseTimes( {
    weeklyHours,
    dayInZone,
    dateInZone,
    timeInZone,
    overrides : OperatingHours.getOverrides( model ),
    timezone
  } )

  return lastCloseTimestamp
}

/**
 * @typedef OperatingHoursResult
 * @property {boolean} isOpen Whether or not the operating hours indicate the entity is open
 * @property {string} dayName The day name from the operating hours that matches the date input. Can be a weekday name or the name assigned to an override date.
 * @property {boolean} isOverride Whether or not the day from the operating hours model matching the time is an override day or a normal weekday.
 * @property {UnitValue|null} timeUntilOpen The unit value representing the number of milliseconds until the next open time, or null if already open
 * @property {UnitValue|null} timeSinceOpen The unit value representing the time since last open in milliseconds, or null if closed
 * @property {UnitValue|null} timeUntilClose The unit value representing the time until close in milliseconds, or null if already closed.
 * @property {UnitValue|null} timeSinceClose The unit value representing the time since the last close in milliseconds, or null if open.
 * @property {Date|null} nextCloseTimestamp A JS Date object representing the next close time, or null if already closed.
 * @property {Date|null} nextOpenTimestamp A JS Date object representing the next open time, or null if already open.
 * @property {Date|null} lastCloseTimestamp A JS Date object representing the last close time, or null if open.
 * @property {Date|null} lastOpenTimestamp A JS Date object representing the last open time, or null if closed.
 * 
 */

/**
 * 
 * @param {object} model The operating hours model to evaluate
 * @param {Date} time A JS Date object to use for evaluation
 * @returns {OperatingHoursResult|null}  An operating hours result object
 */
function getResult( model, time ) {
  const timezone = OperatingHours.getTimezone( model )
  const currentModeName = OperatingHours.getCurrentMode( model )
  const weeklyHours = OperatingHours.getMode( model, currentModeName )

  if ( !weeklyHours ) {
    return null
  }
    
  const timeInZone = DateTime.fromJSDate( time, { zone : timezone } )
  const dayInZone = timeInZone.weekdayLong
  const dateInZone = timeInZone.toFormat( OVERRIDE_STRING_FORMAT )

  if ( !dayInZone ) {
    return null
  }

  let dailyHours = WeeklyHours[`get${dayInZone}`]?.( weeklyHours )
  const override = OperatingHours.getOverride( model, dateInZone )
  dailyHours = override || dailyHours

  if ( !dailyHours ) {
    return null
  }

  const containingRange = getContainingRange( timeInZone, timezone, DailyHours.getRanges( dailyHours ) )

  const isOpen = DailyHours.getOpen( dailyHours ) && containingRange != null

  const ret = {
    isOpen,
    dayName : dayInZone,
    isOverride : !!override,
    timezone
  }

  if ( isOpen ) {
    ret.timeUntilOpen = null
    ret.timeSinceClose = null
    ret.nextOpenTimestamp = null
    ret.lastCloseTime = null

    const { timeUntilClose, nextCloseTimestamp } = getUntilClose( {
      weeklyHours,
      dayInZone,
      dateInZone,
      timeInZone,
      overrides : OperatingHours.getOverrides( model ),
      timezone
    } )
    ret.timeUntilClose = timeUntilClose
    ret.nextCloseTimestamp = nextCloseTimestamp
    const { timeSinceOpen, lastOpenTimestamp } = getSinceOpen( {
      weeklyHours,
      dayInZone,
      dateInZone,
      timeInZone,
      overrides : OperatingHours.getOverrides( model ),
      timezone
    } )
    ret.timeSinceOpen = timeSinceOpen
    ret.lastOpenTimestamp = lastOpenTimestamp
  } else {
    ret.timeUntilClose = null
    ret.timeSinceOpen = null
    const { nextOpenTimestamp, timeUntilOpen, lastCloseTimestamp, timeSinceClose } = getNextOpenAndLastCloseTimes( {
      weeklyHours,
      dayInZone,
      dateInZone,
      timeInZone,
      overrides : OperatingHours.getOverrides( model ),
      timezone
    } )

    ret.nextOpenTimestamp = nextOpenTimestamp
    ret.timeUntilOpen = timeUntilOpen
    ret.lastCloseTimestamp = lastCloseTimestamp
    ret.timeSinceClose = timeSinceClose
  }

  return ret
}

function getNextOpenAndLastCloseTimes( opts ) {
  const { timeInZone } = opts
  const nextOpenTime = getNextDateTime( 'forward', 'start', opts )
  const lastCloseTime = getNextDateTime( 'backward', 'end', opts )

  return {
    nextOpenTimestamp : nextOpenTime?.toJSDate() ?? null,
    timeUntilOpen : nextOpenTime ? {
      type : 'time',
      unit : 'milliseconds',
      value : nextOpenTime.toMillis() - timeInZone.toMillis()
    } : null,
    lastCloseTimestamp : lastCloseTime?.toJSDate() ?? null,
    timeSinceClose : lastCloseTime ? {
      type : 'time',
      unit : 'milliseconds',
      value : timeInZone.toMillis() - lastCloseTime.toMillis()
    } : null
  }
}

/**
 * @param {'forward'|'backward'} direction
 * @param {'start'|'end'} rangeProp
 * @param {object} opts 
 * @param {DateTime} opts.timeInZone
 * @param {string} opts.dayInZone
 * @param {string} opts.dateInZone
 * @param {object} opts.weeklyHours
 * @param {object} opts.overrides
 * @param {string} opts.timezone
 * @returns 
 */
function getNextDateTime( direction, rangeProp, opts ) {
  const { timeInZone, dayInZone, dateInZone, weeklyHours, overrides, timezone } = opts

  const dateFunc = direction === 'forward' ? 'plus' : 'minus'
  
  let lTimeInZone = timeInZone
  let lDayInZone = dayInZone
  let lDateInZone = dateInZone
  let index = 0

  do {
    const todayHours = overrides[lDateInZone] || WeeklyHours[`get${lDayInZone}`]( weeklyHours )
    const isOpen = DailyHours.getOpen( todayHours )
    
    if ( isOpen ) {
      const ranges = DailyHours.getRanges( todayHours )
  
      if ( ranges && ranges.length > 0 ) {
        let earliestStart

        for ( let i = 0; i < ranges.length; i++ ) {
          const range = ranges[i]

          const startTime = DateTime.fromFormat( range[rangeProp], HOURS_STRING_FORMAT, { zone : timezone } )
            .set( {
              day : lTimeInZone.day,
              month : lTimeInZone.month,
              year : lTimeInZone.year
            } )
  
          if ( direction === 'forward' ) {
            if ( timeInZone < startTime ) {
              if ( !earliestStart || startTime < earliestStart ) {
                earliestStart = startTime
              }
            }
          } else if ( timeInZone > startTime ) {
            if ( !earliestStart || startTime > earliestStart ) {
              earliestStart = startTime
            }
          }
        }
  
        if ( earliestStart ) {
          return earliestStart
        }
      }
    }

    lTimeInZone = lTimeInZone[dateFunc]( { days : 1 } )
    lDayInZone = lTimeInZone.weekdayLong
    lDateInZone = lTimeInZone.toFormat( OVERRIDE_STRING_FORMAT )

    index += 1
  } while ( index < 7 )

  return null
}

function getUntilClose( opts ) {
  const { timeInZone } = opts
  const nextClose = getNextDateTime( 'forward', 'end', opts )

  return {
    timeUntilClose : nextClose ? {
      type : 'time',
      unit : 'milliseconds',
      value : nextClose.toMillis() - timeInZone.toMillis()
    } : null,
    nextCloseTimestamp : nextClose?.toJSDate() ?? null
  }
}

function getSinceOpen( opts ) {
  const { timeInZone } = opts
  const lastOpen = getNextDateTime( 'backward', 'start', opts )

  return {
    timeSinceOpen : lastOpen ? {
      type : 'time',
      unit : 'milliseconds',
      value : timeInZone.toMillis() - lastOpen.toMillis()
    } : null,
    lastOpenTimestamp : lastOpen?.toJSDate() ?? null
  }
}

/**
 * 
 * @param {DateTime} time The time to be tested for openness
 * @param {string} timezone The timezone that ranges are in
 * @param {Array} ranges Time ranges in the format of an HoursRange model
 * @returns {boolean}
 */
function getContainingRange( time, timezone, ranges = [] ) {
  if ( ranges.length === 0 ) {
    return false
  }

  return ranges.find( ( range ) => {
    const openHour = HoursRange.getStart( range )
    const closeHour = HoursRange.getEnd( range )
    const openTime = DateTime
      .fromFormat( openHour, HOURS_STRING_FORMAT, { zone : timezone } )
      .set( {
        year : time.year,
        day : time.day,
        month : time.month
      } )
    const closeTime = DateTime
      .fromFormat( closeHour, HOURS_STRING_FORMAT, { zone : timezone } )
      .set( {
        year : time.year,
        day : time.day,
        month : time.month
      } )

    return time >= openTime && time <= closeTime
  } )
}

export {
  getIsOpen,
  getMatchingDayName,
  getIsOverride,
  getTimeUntilOpen,
  getTimeSinceClose,
  getTimeUntilClose,
  getNextCloseTimestamp,
  getTimeSinceOpen,
  getLastOpenTimestamp,
  getNextOpenTimestamp,
  getLastCloseTimestamp,
  getResult,
  getHoursForDate
}
